import React from "react";

import SearchWidgetTabs from "../searchwidgetTabs/searchWidgetTabs";
import gaustaAgeCategories from "./gausta.ageCategories";
import DropdownRoomPicker from "src/@components/dropdown.room.picker";

import DateTime from "src/utils/temporalLuxonUtil";
import { ApplicationContext } from "src/context";
import Icon from "src/components/icon";
import CTAButton from "src/components/ctabutton";
import { DatePickerV2 } from "src/components/datePicker";
import { toFormat, toYYYYMMDD } from "src/utils/datefns";
import queryStringEncoder from "src/utils/queryStringEncoder";


const GaustaSearchWidgetTabs: Array<{
    translations: TTranslations<{ tab_label: string; }>
}> = [
    {
        translations: [
            { language: "en-US", tab_label: "Hotel" },
            { language: "no-NO", tab_label: "Hotell" },
        ]
    },
    {
        translations: [
            { language: "en-US", tab_label: "Cabins and apartments" },
            { language: "no-NO", tab_label: "Hytter og leiligheter" },
        ]
    },
    {
        translations: [
            { language: "en-US", tab_label: "Gausta Skisenter" },
            { language: "no-NO", tab_label: "Gausta Skisenter" },
        ]
    },
    {
        translations: [
            { language: "en-US", tab_label: "Our activities" },
            { language: "no-NO", tab_label: "Våre aktiviteter" },
        ]
    }
]



type GaustaSearchWidgetHotelsProps = {
    rooms: Array<GuestSelectorValue[]> | undefined;
    setRooms: (rooms: Array<GuestSelectorValue[]>) => void;
    adults: number | undefined;
    children: number | undefined;
    ages: number[] | undefined;
    startDate: Date | undefined;
    endDate: Date | undefined;
    setStartDate: (date: Date) => void;
    setEndDate: (date: Date) => void;
    setAdults: (adults: number) => void;
    setChilds: (childs: number, ages: number[]) => void;
    minimumDate: Date;
    maximumDate: Date;
    selectedLanguage: string;
    defaultLanguage: string;
}

type GaustaSearchWidgetHotelsState = {
    option: "0" | "1" | "2" | "3" | "4",
}

class GaustaSearchWidgetHotels extends React.Component<GaustaSearchWidgetHotelsProps, GaustaSearchWidgetHotelsState> {

    /** context: ApplicationContext */
    static contextType = ApplicationContext;
    /** @ts-ignore */
    context!: React.ContextType<typeof ApplicationContext>;

    public state: GaustaSearchWidgetHotelsState = {
        option: "0"
    }

    get link() {
        const rooms = this.props.rooms || [];
        const prefix = (this.context.defaultLanguage !== this.context.language ? '/' + this.context.language : '');
        const uri = prefix + `/hotel/gaustablikk-fjellresort?`;
        const multiRoom = rooms.length > 1;

        // we know that we only have 2 types per gausta. then...
        let rooms_adults = [];
        let rooms_childs = [];
        for (const room of rooms) {
            let adults = 0;
            let childs = [];
            for (const guest of room) {
                if (guest.type === 'adult') adults++;
                else if (guest.type === 'junior') childs.push(guest.age);
            }
            rooms_adults.push(adults);
            rooms_childs.push(childs.join(","));
        }

        const params = multiRoom
            ? {
                startdate: this.startDate,
                enddate: this.endDate,
                childages: rooms_childs.join("-"),
                adult: rooms_adults.join("-"),
                currentRoom: 1,
                numberOfRooms: rooms.length,
            }
            : {
                startdate: this.startDate,
                enddate: this.endDate,
                childages: rooms_childs.join("-"),
                adult: rooms_adults.join("-"),
            };

        return uri + queryStringEncoder(params);
    }

    get startDateAlt() {
        const date = this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return encodeURIComponent(toFormat(date, 'MM/DD/YYYY'));
    }

    get startDate() {
        const date = this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return toYYYYMMDD(date);
    }

    get endDateAlt() {
        const date = this.props.endDate || this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return encodeURIComponent(toFormat(date, 'MM/DD/YYYY'));
    }

    get endDate() {
        const date = this.props.endDate || this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return toYYYYMMDD(date);
    }

    isDayAvailable(date: Date) {
        // No arrival and departures for Saturdays in the period 1st of December 2024 - 27th of April 2025
        const startingDate = new Date(2024, 11, 1);
        const endingDate = new Date(2025, 3, 27);
        if (date < startingDate) return true;
        if (date > endingDate) return true;
        const day = date.getDay();
        if (day === 6) return false;
        return true;
    }

    render() {
        const {startDate, setStartDate, endDate, setEndDate, adults, children, ages, setAdults, setChilds, rooms, setRooms} = this.props;
        return <>
        <div className={`flex flex-col lg:flex-row gap-4 justify-between items-center p-4 sm:p-6 rounded-b-2xl`}>

            <div className="w-full lg:w-1/4">
                <React.Suspense fallback={<div className="w-full h-10" />}>
                    <DatePickerV2
                        isRange={true}
                        onChange={(dates: Date[])=>{ setEndDate(dates[1]); setStartDate(dates[0]); }}
                        startDate={startDate}
                        startLabel={this.context.literal('searchwidget_arrival')}
                        endDate={endDate}
                        endLabel={this.context.literal('searchwidget_departure')}
                        openToDate={startDate}
                        includeDateIntervals={[{ start: this.props.minimumDate, end: this.props.maximumDate }]}
                        isDayBlocked={(data: Date) => this.isDayAvailable(data)}
                        inputBackgroundColor="#A4DCDC"
                        inputPlaceholderColor="#0B2752"
                        calendarChevronColor="#0B2752"
                        inputTextColor="#0B2752"
                        inputBorderColor="#0B2752"
                        inputChevronColor="#0B2752"
                        selectedDayBgColor="#0B2752"
                        selectedDayColor="#FFFFFF"
                    />
                </React.Suspense>
            </div>

            <div className="w-full lg:w-1/4">
                <DropdownRoomPicker
                   rooms={rooms}
                   onChange={setRooms}
                   ageCategories={gaustaAgeCategories}
                   inputColor="#30B2752"
                   backgroundColor="#A4DCDC"
                   borderColor="#0B2752"
                />
            </div>

            <div className="w-full lg:w-1/4">
            </div>

            <div className="w-full lg:w-1/4">
                <CTAButton link={this.link} className="w-full cursor-pointer" roundType="rounded" color="#A4DCDC" bgcolor='#0B2752' hovercolor="#A4DCDC" hoverbgcolor='#0B2752' type="primary" text={this.context.literal('searchwidget_booknow')} icon={<Icon className="w-6 h-4" icon="ArrowRight" />} />
            </div>
          </div>
        </>
    }
}

type GaustaSearchWidgetCabinsProps = {
    setDates: (date1: Date, date2: Date) => void;
    rooms: Array<GuestSelectorValue[]> | undefined;
    setRooms: (rooms: Array<GuestSelectorValue[]>) => void;
    adults: number | undefined;
    children: number | undefined;
    ages: number[] | undefined;
    startDate: Date | undefined;
    endDate: Date | undefined;
    setStartDate: (date: Date) => void;
    setEndDate: (date: Date) => void;
    setAdults: (adults: number) => void;
    setChilds: (childs: number, ages: number[]) => void;
    minimumDate: Date;
    maximumDate: Date;
    selectedLanguage: string;
    defaultLanguage: string;
}

type GaustaSearchWidgetCabinsState = {
    option: "0" | "1" | "2"
}

class GaustaSearchWidgetCabins extends React.Component<GaustaSearchWidgetCabinsProps, GaustaSearchWidgetCabinsState> {

    /** context: ApplicationContext */
    static contextType = ApplicationContext;
    /** @ts-ignore */
    context!: React.ContextType<typeof ApplicationContext>;

    state: GaustaSearchWidgetCabinsState = {
        option: "0",
    }

    get link() {
        const path = this.context.language === 'en-US' ? 'lodging' : 'losji';
        const prefix = (this.context.defaultLanguage !== this.context.language ? '/' + this.context.language : '');
        const uri = prefix + `/${path}?`;

        // we know that we only have 2 types per gausta. then...
        let rooms_adults = [];
        let rooms_childs = [];
        for (const room of (this.props.rooms || [])) {
            let adults = 0;
            let childs = [];
            for (const guest of room) {
                if (guest.type === 'adult') adults++;
                else if (guest.type === 'junior') childs.push(guest.age);
            }
            rooms_adults.push(adults);
            rooms_childs.push(childs.join(","));
        }

        const params = {
            startdate: this.startDate,
            enddate: this.endDate,
            childages: rooms_childs.join("-"),
            adult: rooms_adults.join("-"),
        }

        return uri + queryStringEncoder(params);
    }

    get startDateAlt() {
        const date = this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return encodeURIComponent(toFormat(date, 'MM/DD/YYYY'));
    }

    get startDate() {
        const date = this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return toYYYYMMDD(date);
    }

    get endDateAlt() {
        const date = this.props.endDate || this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return encodeURIComponent(toFormat(date, 'MM/DD/YYYY'));
    }

    get endDate() {
        const date = this.props.endDate || this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return toYYYYMMDD(date);
    }

    isDayAvailable(date: Date) {
        if (this.props.endDate) return true;
        if (!this.props.startDate) return true;
        const et = date.getTime();
        const st = this.props.startDate.getTime();
        const diff = et - st;
        if (diff < 0) return false;
        return (date.getTime() - this.props.startDate.getTime()) > (86400001);
    }


    render() {
        const {startDate, setStartDate, endDate, setEndDate, setDates, adults, children, ages, setAdults, setChilds, rooms, setRooms} = this.props;
        return <>
        <div className={`flex flex-col lg:flex-row gap-4 justify-between items-center p-4 sm:p-6 rounded-b-2xl`}>

            <div className="w-full lg:w-1/4">
                <React.Suspense fallback={<div className="w-full h-10" />}>
                    <DatePickerV2
                        isRange={true}
                        onChange={(dates: Date[])=>{ setDates(dates[0], dates[1]); }}
                        startDate={startDate}
                        startLabel={this.context.literal('searchwidget_arrival')}
                        endDate={endDate}
                        endLabel={this.context.literal('searchwidget_departure')}
                        openToDate={startDate}
                        includeDateIntervals={[{ start: this.props.minimumDate, end: this.props.maximumDate }]}
                        weekdaysBlocked={[6]}
                        isDayBlocked={(data: Date) => this.isDayAvailable(data)}
                        inputBackgroundColor="#A4DCDC"
                        inputPlaceholderColor="#0B2752"
                        calendarChevronColor="#0B2752"
                        inputTextColor="#0B2752"
                        inputBorderColor="#0B2752"
                        inputChevronColor="#0B2752"
                        selectedDayBgColor="#0B2752"
                        selectedDayColor="#FFFFFF"
                    />
              </React.Suspense>
            </div>

            <div className="w-full lg:w-1/4">
                <DropdownRoomPicker
                   rooms={rooms}
                   onChange={setRooms}
                   ageCategories={gaustaAgeCategories}
                   inputColor="#30B2752"
                   backgroundColor="#A4DCDC"
                   borderColor="#0B2752"
                />
            </div>

            <div className="w-full lg:w-1/4">
            </div>

            <div className="w-full lg:w-1/4">
                <CTAButton link={this.link} className="w-full cursor-pointer" roundType="rounded" color="#A4DCDC" bgcolor='#0B2752' hovercolor="#A4DCDC" hoverbgcolor='#0B2752' type="primary" text={this.context.literal('searchwidget_booknow')} icon={<Icon className="w-6 h-4" icon="ArrowRight" />} />
            </div>
          </div>
        </>
    }
}

type GaustaSkisenterProps = {
    selectedLanguage: string;
    defaultLanguage: string
}

type GaustaSkisenterState = {
    option: "0" | "1" | "2";
}

class GaustaSkisenter extends React.Component<GaustaSkisenterProps, GaustaSkisenterState> {

    /** context: ApplicationContext */
    static contextType = ApplicationContext;
    /** @ts-ignore */
    context!: React.ContextType<typeof ApplicationContext>;

    state: GaustaSkisenterState = {
        option: "0",
    }

    get options() {
        if (this.context.language === 'en-US') return [
            { label: "Ski pass", value: '0' },
            { label: "Ski school", value: '1' },
            { label: "Ski rental", value: '2' },
        ]
        return [
            {label: "Heiskort", value: '0' },
            {label: "Skiskole", value: '1' },
            {label: "Skiutleie", value: '2' },
        ];
    }

    get link() {
        if (this.context.language === 'en-US') {
            if (this.state.option === '0') { return "/en-US/ski-pass"; }
            if (this.state.option === '1') { return "/en-US/lessons-product-first"; }
            if (this.state.option === '2') { return "/en-US/book-ski-rental"; }
        }
        if (this.state.option === '0') { return "/heiskort"; }
        if (this.state.option === '1') { return "/lessons-product-first"; }
        if (this.state.option === '2') { return "/book-skiutleie"; }
        return "";
    }

    render() {
        return <>
        <div className={`flex flex-col lg:flex-row gap-4 justify-between items-center p-4 sm:p-6 rounded-b-2xl`}>
            <div className="w-full lg:w-1/4">
                <select value={this.state.option} onChange={(e) => this.setState({option: e.target.value as any})} className="w-full rounded-lg bg-[#A4DCDC] border-[#0B2752] text-[#0B2752] global-bselect caption h-10">
                    {this.options.map(option => <option key={`opt-${option.value}`} value={option.value}>{option.label}</option>)}
                </select>
            </div>
            <div className="w-full lg:w-1/4"></div>
            <div className="w-full lg:w-1/4"></div>
            <div className="w-full lg:w-1/4">
                <CTAButton link={this.link} className="w-full cursor-pointer" roundType="rounded" color="#A4DCDC" bgcolor='#0B2752' hovercolor="#A4DCDC" hoverbgcolor='#0B2752' type="primary" text={this.context.literal('searchwidget_booknow')} icon={<Icon className="w-6 h-4" icon="ArrowRight" />} />
            </div>
          </div>
        </>
    }
}

type GaustaActivitiesProps = {
    adults: number | undefined;
    children: number | undefined;
    ages: number[] | undefined;
    startDate: Date | undefined;
    endDate: Date | undefined;
    setStartDate: (date: Date) => void;
    setEndDate: (date: Date) => void;
    setAdults: (adults: number) => void;
    setChilds: (childs: number, ages: number[]) => void;
    minimumDate: Date;
    maximumDate: Date;
    selectedLanguage: string;
    defaultLanguage: string;
}

type GaustaActivitiesState = {
    option: "0" | "1" | "2" | "3" | "4"
}


class GaustaActivities extends React.Component<GaustaActivitiesProps, GaustaActivitiesState> {
    /** context: ApplicationContext */
    static contextType = ApplicationContext;
    /** @ts-ignore */
    context!: React.ContextType<typeof ApplicationContext>;

    public state: GaustaActivitiesState = {
        option: "0"
    }


    get link() {
        const path = this.context.language === 'en-US' ? 'activities' : 'aktiviteter';
        const prefix = (this.context.defaultLanguage !== this.context.language ? '/' + this.context.language : '');
        const uri = prefix + `/${path}?`;
        const params = { startdate: this.startDate };
        return uri + queryStringEncoder(params);
    }

    get startDateAlt() {
        const date = this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return encodeURIComponent(toFormat(date, 'MM/DD/YYYY'));
    }

    get startDate() {
        const date = this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return toYYYYMMDD(date);
    }

    get endDateAlt() {
        const date = this.props.endDate || this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return encodeURIComponent(toFormat(date, 'MM/DD/YYYY'));
    }

    get endDate() {
        const date = this.props.endDate || this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return toYYYYMMDD(date);
    }

    render() {
        const {startDate, setStartDate, endDate, setEndDate, adults, children, ages, setAdults, setChilds} = this.props;

        return <>
        <div className={`flex flex-col lg:flex-row gap-4 justify-between items-center p-4 sm:p-6 rounded-b-2xl`}>
            <div className="w-full lg:w-1/4">
                <React.Suspense fallback={<div className="w-full h-10" />}>
                    <DatePickerV2
                        isRange={false}
                        onChange={setStartDate}
                        value={startDate}
                        label={this.context.literal('searchwidget_arrival')}
                        openToDate={startDate}
                        includeDateIntervals={[{ start: this.props.minimumDate, end: this.props.maximumDate }]}
                        inputBackgroundColor="#A4DCDC"
                        inputPlaceholderColor="#0B2752"
                        calendarChevronColor="#0B2752"
                        inputTextColor="#0B2752"
                        inputBorderColor="#0B2752"
                        inputChevronColor="#0B2752"
                        selectedDayBgColor="#0B2752"
                        selectedDayColor="#FFFFFF"
                    />
              </React.Suspense>
            </div>
            <div className="w-full lg:w-1/4"></div>
            <div className="w-full lg:w-1/4"></div>
            <div className="w-full lg:w-1/4">
                <CTAButton link={this.link} className="w-full cursor-pointer" roundType="rounded" color="#A4DCDC" bgcolor='#0B2752' hovercolor="#A4DCDC" hoverbgcolor='#0B2752' type="primary" text={this.context.literal('searchwidget_booknow')} icon={<Icon className="w-6 h-4" icon="ArrowRight" />} />
            </div>
        </div>
        </>
    }
}


type SearchWidgetProps = {
    selectedLanguage: string;
    defaultLanguage: string;
    removeMargin: boolean;
}

type SearchWidgetState = {
    tab: "wildpasses" | "rides" | "tickets" | "stay" | "addons";
    startDate: Date | undefined;
    endDate: Date | undefined;
    rooms: Array<GuestSelectorValue[]> | undefined;
    adults: number | undefined;
    children: number | undefined;
    ages: number[] | undefined;
}

export default class GaustaSearchWidget extends React.Component<SearchWidgetProps, SearchWidgetState> {

    /** context: ApplicationContext */
    static contextType = ApplicationContext;
    /** @ts-ignore */
    context!: React.ContextType<typeof ApplicationContext>;

    static minimumDate() {
        const baseDate = new Date();
        const hour = baseDate.getHours();
        const today = DateTime.fromJSDate(baseDate).startOf("day").toJSDate();
        const tomorrow = DateTime.fromJSDate(baseDate).startOf("day").plus({ day: 1 }).startOf("day").toJSDate();
        const minimumStart = hour >= 15 ? tomorrow : today;
        return minimumStart;
    }

    static startsToday() {
        const baseDate = new Date();
        const tomorrow = DateTime.fromJSDate(baseDate).startOf("day").startOf("day").toJSDate();
        return tomorrow;
    }

    static startsTodayand2() {
        const baseDate = new Date();
        const tomorrow = DateTime.fromJSDate(baseDate).startOf("day").plus({ day: 2 }).startOf("day").toJSDate();
        return tomorrow;
    }

    static startsTomorrow() {
        const baseDate = new Date();
        const tomorrow = DateTime.fromJSDate(baseDate).startOf("day").plus({ day: 1 }).startOf("day").toJSDate();
        return tomorrow;
    }

    static startsTomorrowand2() {
        const baseDate = new Date();
        const tomorrow = DateTime.fromJSDate(baseDate).startOf("day").plus({ day: 3 }).startOf("day").toJSDate();
        return tomorrow;
    }

    static startsTomorrowand3() {
        const baseDate = new Date();
        const tomorrow = DateTime.fromJSDate(baseDate).startOf("day").plus({ day: 4 }).startOf("day").toJSDate();
        return tomorrow;
    }

    static getEndDate() {
        return DateTime.fromJSDate(GaustaSearchWidget.minimumDate()).plus({day: 1}).toJSDate()
    }

    static maximumDate() {
        const baseDate = new Date();
        const nextYears = DateTime.fromJSDate(baseDate).startOf("day").plus({ year: 2 }).startOf("day").toJSDate();
        return nextYears;
    }

    state: SearchWidgetState = {
        tab: "stay",
        rooms: DropdownRoomPicker.getDefaultValue(gaustaAgeCategories, {"adult": 1}),
        startDate: GaustaSearchWidget.startsToday(),
        endDate: GaustaSearchWidget.startsTodayand2(),
        adults: 0,
        children: 0,
        ages: undefined,
    }

    setStartDate = (date: Date) => {
        this.setState({startDate: date});
    }

    setDates = (date1: Date, date2: Date) => {
        this.setState({ startDate: date1, endDate: date2 });
    }

    setEndDate = (date: Date) => this.setState({endDate: date})
    setAdults = (adults: number) => this.setState({adults})
    setChilds = (childs: number, ages: number[]) => this.setState({children: childs, ages})
    setRooms = (rooms: Array<GuestSelectorValue[]>) => this.setState({ rooms })

    reinitializeDates = (newtab: number) => {
        if (newtab === 0) {
            this.setState({startDate: GaustaSearchWidget.startsToday(), endDate: GaustaSearchWidget.startsTodayand2() })
        } else if (newtab === 1) {
            this.setState({
                startDate: GaustaSearchWidget.startsTomorrow().getDay() === 6 ? GaustaSearchWidget.startsTodayand2() : GaustaSearchWidget.startsTomorrow(),
                endDate: GaustaSearchWidget.startsTomorrow().getDay() === 6 ? GaustaSearchWidget.startsTomorrowand3() : GaustaSearchWidget.startsTomorrowand2()
            })
        } else {
            this.setState({startDate: GaustaSearchWidget.startsToday() })
        }
    }

    render() {
        return <>
        <div className={`${this.props.removeMargin ? "" : "mt-[-88px]"} flex w-full justify-center lg:px-10 relative z-[2] flex-col`}>
            <div className="w-[95%] sm:max-w-[400px] lg:max-w-6xl justify-center m-auto flex flex-col">
                <SearchWidgetTabs
                    onTabChange={(tab) => this.reinitializeDates(tab)}
                    activeTabLineColor="#0B2752"
                    activeFocusTextColor="#0B2752"
                    activeFocusBackgroundColor="#A4DCDC"
                    desktopActiveTabTextColor="#0B2752"
                    desktopActiveTabBackgroundColor="#A4DCDC"
                    desktopInactiveTabTextColor="#A4DCDC"
                    desktopInactiveTabBackgroundColor="#0B2752"
                    mobileActiveTabBackgroundColor="#A4DCDC"
                    mobileActiveTabTextColor="#0B2752"
                    mobileInactiveTabBackgroundColor="#b0f0f1"
                    mobileInactiveTabTextColor="#0B2752"
                    desktopBackgroundColor="#A4DCDC"
                    mobileBackgroundColor="#A4DCDC"
                >
                    <SearchWidgetTabs.Tab
                        label={this.context.translate(GaustaSearchWidgetTabs[0].translations, 'tab_label')}
                        icon={<Icon width={26} height={26} icon="Hotel" />}
                        desktopLabelClassname="searchWidgetText text-center" mobileLabelClassname="searchWidgetText"
                    >
                        <GaustaSearchWidgetHotels
                            rooms={this.state.rooms}
                            setRooms={this.setRooms}
                            startDate={this.state.startDate}
                            endDate={this.state.endDate}
                            setStartDate={this.setStartDate}
                            setEndDate={this.setEndDate}
                            adults={this.state.adults}
                            setAdults={this.setAdults}
                            children={this.state.children}
                            setChilds={this.setChilds}
                            ages={this.state.ages}
                            minimumDate={GaustaSearchWidget.minimumDate()}
                            maximumDate={GaustaSearchWidget.maximumDate()}
                            selectedLanguage={this.props.selectedLanguage}
                            defaultLanguage={this.props.defaultLanguage}
                        />
                    </SearchWidgetTabs.Tab>
                    <SearchWidgetTabs.Tab
                        label={this.context.translate(GaustaSearchWidgetTabs[1].translations, 'tab_label')}
                        icon={<Icon width={26} height={26} icon="Cabin" />}
                        desktopLabelClassname="searchWidgetText text-center" mobileLabelClassname="searchWidgetText"
                    >
                        <GaustaSearchWidgetCabins
                            rooms={this.state.rooms}
                            setRooms={this.setRooms}
                            setDates={this.setDates}
                            startDate={this.state.startDate}
                            endDate={this.state.endDate}
                            setStartDate={this.setStartDate}
                            setEndDate={this.setEndDate}
                            adults={this.state.adults}
                            setAdults={this.setAdults}
                            children={this.state.children}
                            setChilds={this.setChilds}
                            ages={this.state.ages}
                            minimumDate={GaustaSearchWidget.startsTomorrow()}
                            maximumDate={GaustaSearchWidget.maximumDate()}
                            selectedLanguage={this.props.selectedLanguage}
                            defaultLanguage={this.props.defaultLanguage}
                        />
                    </SearchWidgetTabs.Tab>
                    <SearchWidgetTabs.Tab
                        label={this.context.translate(GaustaSearchWidgetTabs[2].translations, 'tab_label')}
                        icon={<Icon width={26} height={26} icon="Skisenter" />}
                        desktopLabelClassname="searchWidgetText text-center" mobileLabelClassname="searchWidgetText"
                    >
                        <GaustaSkisenter
                            selectedLanguage={this.props.selectedLanguage}
                            defaultLanguage={this.props.defaultLanguage}
                        />
                    </SearchWidgetTabs.Tab>
                    <SearchWidgetTabs.Tab
                        label={this.context.translate(GaustaSearchWidgetTabs[3].translations, 'tab_label')}
                        icon={<Icon width={26} height={26} icon="Boot" />}
                        desktopLabelClassname="searchWidgetText text-center" mobileLabelClassname="searchWidgetText"
                    >
                        <GaustaActivities
                            startDate={this.state.startDate}
                            endDate={this.state.endDate}
                            setStartDate={this.setStartDate}
                            setEndDate={this.setEndDate}
                            adults={this.state.adults}
                            setAdults={this.setAdults}
                            children={this.state.children}
                            setChilds={this.setChilds}
                            ages={this.state.ages}
                            minimumDate={GaustaSearchWidget.minimumDate()}
                            maximumDate={GaustaSearchWidget.maximumDate()}
                            selectedLanguage={this.props.selectedLanguage}
                            defaultLanguage={this.props.defaultLanguage}
                        />
                    </SearchWidgetTabs.Tab>
                </SearchWidgetTabs>
            </div>
        </div>
        </>
    }

}